const CHANNEL_LABELS = ['Correspondent', 'Wholesale', 'Non-Delegated'] as const;
const CHANNEL_ALT = CHANNEL_LABELS.join('|');
const STRUCTURED_CHANNEL_SUFFIX = new RegExp(
  `\\s+-\\s+(?:${CHANNEL_ALT})(?:\\s+-\\s*.*|\\s*)$`,
  'i'
);
const STRUCTURED_PRODUCT_PREFIX = new RegExp(
  `^.+?\\s+-\\s+(?:${CHANNEL_ALT})\\s+-\\s*(.+)$`,
  'i'
);
const LEADING_CHANNEL_PREFIX = new RegExp(
  `^(?:${CHANNEL_ALT})\\s+-\\s*`,
  'i'
);

export interface ObProductDisplayLabels {
  displayInvestor: string;
  displayProductName: string;
}

function cleanText(value: string | null | undefined): string {
  return String(value ?? '').replace(/\s+/g, ' ').trim();
}

function stripTrailingDashNoise(value: string): string {
  return value.replace(/(?:\s+-\s*)+$/g, '').trim();
}

function stripPrefix(value: string, prefix: string): string | null {
  const text = cleanText(value);
  const normalizedPrefix = stripTrailingDashNoise(cleanText(prefix));
  if (!text || !normalizedPrefix) {
    return null;
  }
  if (text.toLowerCase() === normalizedPrefix.toLowerCase()) {
    return '';
  }
  if (!text.toLowerCase().startsWith(normalizedPrefix.toLowerCase())) {
    return null;
  }
  const remainder = text.slice(normalizedPrefix.length);
  if (!/^\s*-/.test(remainder)) {
    return null;
  }
  return remainder.replace(/^\s*(?:-\s*)+/, '').trim();
}

export function displayObInvestorLabel(investor: string | null | undefined): string {
  const text = stripTrailingDashNoise(cleanText(investor));
  if (!text) {
    return '';
  }
  return stripTrailingDashNoise(text.replace(STRUCTURED_CHANNEL_SUFFIX, '').trim());
}

export function displayObProductName(
  productName: string | null | undefined,
  rawInvestor?: string | null
): string {
  const text = cleanText(productName);
  if (!text) {
    return '';
  }

  const byRawInvestor = stripPrefix(text, rawInvestor ?? '');
  if (byRawInvestor !== null) {
    return byRawInvestor.replace(LEADING_CHANNEL_PREFIX, '').trim();
  }

  const displayInvestor = displayObInvestorLabel(rawInvestor);
  const byDisplayInvestor = stripPrefix(text, displayInvestor);
  if (byDisplayInvestor !== null) {
    return byDisplayInvestor.replace(LEADING_CHANNEL_PREFIX, '').trim();
  }

  const structured = text.match(STRUCTURED_PRODUCT_PREFIX);
  if (structured && structured[1]) {
    return structured[1].trim();
  }

  return text;
}

export function displayObProductLabels(
  rawInvestor: string | null | undefined,
  rawProductName: string | null | undefined
): ObProductDisplayLabels {
  return {
    displayInvestor: displayObInvestorLabel(rawInvestor),
    displayProductName: displayObProductName(rawProductName, rawInvestor)
  };
}
